import React, { useState, useEffect, useCallback } from 'react';
import { X, BookOpen, Tag, ListFilter, FileText, Edit, Loader2 } from 'lucide-react';

// Importer les onglets convertis en Tailwind
import OverviewTab from './OverviewTab.js';
import ConceptsTab from './ConceptsTab.js';
import ObjectivesTab from './ObjectivesTab.js';
import NotesTab from './NotesTab.js';

const BackEndUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';

const DocumentDetail = ({ documentId, userId, competenceId, onClose, makeAuthenticatedRequest, onError }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [document, setDocument] = useState(null);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [userNotes, setUserNotes] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Récupérer les détails du document
  const fetchDocumentDetails = useCallback(async () => {
    if (!documentId) return;
    
    try {
      setLoading(true);
      
      const response = await makeAuthenticatedRequest(
        `${BackEndUrl}/api/getDocument/${documentId}`
      );
      
      const data = await response.json();
      
      if (!data.success || !data.document) {
        throw new Error('Erreur lors de la récupération du document');
      }
      
      setDocument(data.document);
      setAnalysisResults(data.document.analysis || {});
      setUserNotes(data.document.metadata?.userNotes || '');
      
    } catch (error) {
      onError(error, 'Chargement des détails du document');
    } finally {
      setLoading(false);
    }
  }, [documentId, makeAuthenticatedRequest, onError]);
  
  // Charger les données du document au montage
  useEffect(() => {
    fetchDocumentDetails();
  }, [fetchDocumentDetails]);
  
  // Enregistrer les notes utilisateur
  const saveNotes = useCallback(async () => {
    if (!documentId) return;
    
    try {
      const response = await makeAuthenticatedRequest(
        `${BackEndUrl}/api/updateDocumentNotes/${documentId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ notes: userNotes })
        }
      );
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error('Erreur lors de l\'enregistrement des notes');
      }
      
    } catch (error) {
      onError(error, 'Enregistrement des notes');
    }
  }, [documentId, userNotes, makeAuthenticatedRequest, onError]);
  
  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: <BookOpen className="h-4 w-4" /> },
    { id: 'concepts', label: 'Concepts', icon: <Tag className="h-4 w-4" /> },
    { id: 'objectives', label: 'Objectifs', icon: <ListFilter className="h-4 w-4" /> },
    { id: 'notes', label: 'Notes', icon: <Edit className="h-4 w-4" /> },
  ];
  
  // Si document est chargé mais n'a pas d'objectifs, ne pas afficher cet onglet
  const filteredTabs = document && (!document.analysis?.objectives || document.analysis.objectives.length === 0)
    ? tabs.filter(tab => tab.id !== 'objectives')
    : tabs;
  
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-4xl flex flex-col items-center justify-center">
        <Loader2 className="h-10 w-10 text-blue-500 animate-spin" />
        <p className="mt-4 text-gray-600">Chargement des détails du document...</p>
      </div>
    );
  }
  
  if (!document) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-4xl">
        <div className="text-center text-gray-600">
          <FileText className="h-16 w-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-medium mb-2">Document introuvable</h3>
          <p>Ce document n'existe plus ou vous n'avez pas les permissions nécessaires.</p>
          <button
            onClick={onClose}
            className="mt-6 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden w-full max-w-4xl flex flex-col max-h-[90vh]">
      {/* En-tête avec titre du document et bouton de fermeture */}
      <div className="p-4 border-b flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            {document.metadata?.title || document.fileName || 'Détails du document'}
          </h2>
          <p className="text-sm text-gray-500">
            {document.metadata?.documentType || 'Document'} · {document.metadata?.pageCount || '—'} pages
          </p>
        </div>
        <button 
          onClick={onClose} 
          className="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      
      {/* Onglets */}
      <div className="border-b">
        <div className="flex overflow-x-auto px-4">
          {filteredTabs.map(tab => (
            <button
              key={tab.id}
              className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors ${
                activeTab === tab.id 
                  ? 'border-b-2 border-blue-500 text-blue-600' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      
      {/* Contenu de l'onglet */}
      <div className="p-6 overflow-y-auto flex-grow">
        {activeTab === 'overview' && (
          <OverviewTab 
            documentData={document} 
            analysisResults={analysisResults}
            makeAuthenticatedRequest={makeAuthenticatedRequest}
          />
        )}
        {activeTab === 'concepts' && (
          <ConceptsTab 
            documentData={document} 
            analysisResults={analysisResults}
          />
        )}
        {activeTab === 'objectives' && (
          <ObjectivesTab 
            documentData={document} 
            analysisResults={analysisResults}
          />
        )}
        {activeTab === 'notes' && (
          <NotesTab 
            userNotes={userNotes} 
            setUserNotes={setUserNotes} 
            saveNotes={saveNotes} 
            documentData={document}
            makeAuthenticatedRequest={makeAuthenticatedRequest}
          />
        )}
      </div>
    </div>
  );
};

export default DocumentDetail;